import { Store } from "vuex";
import { store } from "@/shared-store";
import { IVuexState } from "@/apps/merchant3/shared-models/IVuex";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";
import { MerchantCountry } from "@/types/merchant";

/**
 * Validates if a string is a valid country code (2 letters)
 */
export const isValidCountryCode = (
  code: string | null | undefined
): boolean => {
  if (!code) return false;
  return /^[A-Z]{2}$/i.test(code);
};

/**
 * Gets country code from Vuex store (merchant state)
 */
const getCountryFromStore = (): string | null => {
  const vuexStore = store as unknown as Store<IVuexState>;
  const country = vuexStore?.state?.merchant?.merchant?.country;
  if (country && isValidCountryCode(country)) {
    return country.toLowerCase();
  }
  return null;
};

/**
 * Gets country code from session storage
 */
const getCountryFromSession = (): string | null => {
  const country = sessionStorage.getItem(SessionStorageEnum.MerchantCountry);
  if (country && isValidCountryCode(country)) {
    return country.toLowerCase();
  }
  return null;
};

/**
 * Gets country code from localStorage (preserved during logout)
 */
const getCountryFromLocalStorage = (): string | null => {
  const country = localStorage.getItem(SessionStorageEnum.MerchantCountry);
  if (country && isValidCountryCode(country)) {
    return country.toLowerCase();
  }
  return null;
};

/**
 * Centralized country code resolution.
 * Priority: store > sessionStorage > localStorage > MX default
 * Always returns lowercase 2-letter code.
 */
export const getCountryCode = (): string => {
  const storeCountry = getCountryFromStore();
  if (storeCountry) {
    return storeCountry;
  }

  const sessionCountry = getCountryFromSession();
  if (sessionCountry) {
    return sessionCountry;
  }

  const localStorageCountry = getCountryFromLocalStorage();
  if (localStorageCountry) {
    return localStorageCountry;
  }

  return MerchantCountry.MX.toLowerCase();
};

/**
 * Backward-compatible wrapper for existing callers.
 * Returns country code from centralized resolution.
 * @param lowerCase - if true returns lowercase (default behavior of getCountryCode),
 *                    if false/undefined returns uppercase
 */
export const getCountry = (lowerCase?: boolean): string => {
  const code = getCountryCode();
  return lowerCase ? code : code.toUpperCase();
};
